import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import tenantAdminService from '../../services/tenantAdminService';
import { getApiErrorMessage } from '../../utils/apiError';
import { setTenantAdminGate } from '../../utils/tenantAdminGate';

interface TenantAdminGateDialogProps {
  onUnlocked: () => void;
}

const TenantAdminGateDialog: React.FC<TenantAdminGateDialogProps> = ({ onUnlocked }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = password.trim();
    if (!trimmed) {
      setError('Ingrese la contraseña de acceso.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const result = await tenantAdminService.verifyAccess(trimmed);
      if (result.gateRequired && result.gateToken) {
        setTenantAdminGate(result.gateToken, result.expiresAt);
      }
      onUnlocked();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError('Contraseña incorrecta.');
      } else {
        setError(getApiErrorMessage(err, 'No se pudo verificar el acceso.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} component="form" onSubmit={handleSubmit}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LockIcon color="primary" />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Acceso restringido
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              La administración de tenants requiere una contraseña adicional. Solo usuarios con rol
              SUPERADMIN pueden continuar.
            </Typography>
            <TextField
              label="Contraseña de administración"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              autoFocus
              disabled={submitting}
              error={Boolean(error)}
              helperText={error ?? ' '}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSubmit(e);
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {submitting ? 'Verificando…' : 'Ingresar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TenantAdminGateDialog;
